import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, MessageCircle, Mail, Phone, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import EmailModal from '../components/EmailModal';

const HelpSupport = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);

  const handleEmailClick = () => {
    try {
      window.location.href = 'mailto:customer-service@edgetrades.site?subject=Support Request - EdgeTrade&body=Hello EdgeTrade Support Team,%0D%0A%0D%0AI need assistance with:%0D%0A%0D%0A';
      setTimeout(() => {
        setShowEmailModal(true);
      }, 1000);
    } catch (error) {
      setShowEmailModal(true);
    }
  };

  const faqCategories = [
    {
      title: 'Getting Started',
      questions: [
        { q: 'How do I create an account?', a: 'Click on Register and fill in your details.' },
        { q: 'How do I verify my account?', a: 'Go to Settings > Security and complete KYC verification.' },
      ]
    },
    {
      title: 'Trading',
      questions: [
        { q: 'How do I place a trade?', a: 'Select a trading pair, choose buy/sell, enter amount and confirm.' },
        { q: 'What is futures trading?', a: 'Futures allow you to trade with leverage up to 100x.' },
      ]
    },
    {
      title: 'Wallet & Payments',
      questions: [
        { q: 'How do I deposit funds?', a: 'Go to Wallet > Deposit and follow the instructions.' },
        { q: 'How long do withdrawals take?', a: 'Withdrawals are processed within 24 hours.' },
      ]
    },
  ];

  const handleLiveChatClick = () => {
    navigate('/live-chat');
  };

  const contactMethods = [
    { icon: MessageCircle, label: 'Live Chat', description: 'Chat with our support team', color: 'from-blue-400 to-blue-500', action: handleLiveChatClick },
    { icon: Mail, label: 'Email Support', description: 'customer-service@edgetrades.site', color: 'from-purple-400 to-purple-500', action: handleEmailClick },
    { icon: Phone, label: '24/7 Support', description: 'Email us anytime', color: 'from-green-400 to-green-500', action: handleEmailClick },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-cyan-950 to-blue-900 pb-20">
      <div className="sticky top-0 bg-gradient-to-r from-slate-900 via-blue-900 to-cyan-900 backdrop-blur-sm border-b border-cyan-500/20 z-10">
        <div className="flex items-center justify-between px-3 sm:px-4 py-3 sm:py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 sm:gap-2 text-cyan-400 hover:text-cyan-300">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="font-semibold text-sm sm:text-base">Back</span>
          </button>
          <h1 className="text-base sm:text-lg font-bold text-cyan-400">Help & Support</h1>
          <div className="w-12 sm:w-16"></div>
        </div>
      </div>

      <div className="px-3 sm:px-4 py-4 sm:py-6 max-w-4xl mx-auto">
        {/* Search */}
        <div className="mb-4 sm:mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
            <Input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 sm:pl-10 h-10 sm:h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700 text-sm sm:text-base"
            />
          </div>
        </div>

        {/* Contact Methods */}
        <div className="mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-bold text-cyan-400 mb-3 sm:mb-4">Contact Us</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {contactMethods.map((method, idx) => {
              const Icon = method.icon;
              return (
                <button
                  key={idx}
                  onClick={method.action}
                  className="w-full bg-gradient-to-br from-slate-800/80 to-blue-900/40 border border-cyan-500/30 rounded-xl p-4 hover:border-cyan-400/50 hover:shadow-lg hover:shadow-cyan-500/20 transition-all text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br ${method.color} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-cyan-300 text-sm sm:text-base">{method.label}</div>
                      <div className="text-xs text-cyan-600 truncate">{method.description}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <h2 className="text-base sm:text-lg font-bold text-cyan-400 mb-3 sm:mb-4">Frequently Asked Questions</h2>
          {faqCategories.map((category, catIdx) => (
            <div key={catIdx} className="mb-4">
              <h3 className="text-sm sm:text-md font-semibold text-cyan-300 mb-2 sm:mb-3">{category.title}</h3>
              {category.questions.map((faq, qIdx) => (
                <details key={qIdx} className="bg-gradient-to-br from-slate-800/80 to-blue-900/40 border border-cyan-500/30 rounded-xl p-3 sm:p-4 mb-2 group">
                  <summary className="flex items-center justify-between cursor-pointer text-cyan-300 font-medium text-sm sm:text-base">
                    <span className="pr-2">{faq.q}</span>
                    <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                  </summary>
                  <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-cyan-600 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          ))}
        </div>
      </div>
      
      {showEmailModal && (
        <EmailModal 
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
        />
      )}
    </div>
  );
};

export default HelpSupport;